import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { DocumentSummary } from '../../types/dashboard';
import DocumentRow from './DocumentRow';
import CardScrollBody from './CardScrollBody';
import { DASHBOARD_CARD_HEIGHT, DASHBOARD_CARD_HEADER } from './cardHeight';

interface RecentDocumentsCardProps {
  documents: DocumentSummary[];
  loading?: boolean;
  onSelect?: (id: string) => void;
  onViewAll?: () => void;
  onUpload?: () => void;
}

const RecentDocumentsCard: React.FC<RecentDocumentsCardProps> = ({
  documents,
  loading = false,
  onSelect,
  onViewAll,
  onUpload,
}) => {
  const { t } = useTranslation();

  return (
    <Card className={`flex ${DASHBOARD_CARD_HEIGHT} flex-col overflow-hidden`}>
      <CardHeader
        className={`${DASHBOARD_CARD_HEADER} flex-row items-center justify-between space-y-0 border-b py-0`}
      >
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <FileText className="size-4 text-muted-foreground" strokeWidth={1.9} />
          {t('dashboard.recentDocuments')}
        </CardTitle>
        <div className="flex items-center gap-2">
          {!loading && documents.length > 0 && (
            <Badge variant="secondary" className="revy-figure font-normal">
              {documents.length}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewAll}
            className="-mr-2 h-8 text-muted-foreground"
          >
            {t('dashboard.viewAll')}
            <ArrowRight className="size-3.5" />
          </Button>
        </div>
      </CardHeader>

      <CardScrollBody label={t('dashboard.recentDocuments')}>
      {loading ? (
        <div className="space-y-1 p-2">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="flex items-center gap-3 px-2 py-2.5">
              <Skeleton className="size-9 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground">
            <FileText className="size-5" strokeWidth={1.6} />
          </span>
          <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
            {t('dashboard.noRecentDocuments')}
          </p>
          {onUpload && (
            <Button variant="outline" size="sm" onClick={onUpload}>
              {t('navigation.uploadDocument')}
            </Button>
          )}
        </div>
      ) : (
        <div className="p-2">
          {documents.map((doc) => (
            <DocumentRow key={doc.id} document={doc} onSelect={onSelect} />
          ))}
        </div>
      )}
      </CardScrollBody>
    </Card>
  );
};

export default RecentDocumentsCard;
